import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useOffline } from '../../contexts/OfflineContext';
import { getPosLayoutConfig } from '../../lib/api';
import { normalizePosHomeShortcuts, resolvePosHomeIcon } from '../../lib/posHomeScreen';
import { RefreshCw, Wifi, WifiOff } from 'lucide-react';
import { toast } from 'sonner';

export default function PosMobileHome() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isOnline, isSyncing, pendingOrdersCount, syncData } = useOffline();
  const [homeShortcuts, setHomeShortcuts] = useState(() => normalizePosHomeShortcuts([]));

  useEffect(() => {
    let cancelled = false;

    getPosLayoutConfig()
      .then((config) => {
        if (cancelled) return;
        setHomeShortcuts(normalizePosHomeShortcuts(config?.homeShortcuts));
      })
      .catch(() => {
        if (cancelled) return;
        setHomeShortcuts(normalizePosHomeShortcuts([]));
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const visibleShortcuts = useMemo(
    () => homeShortcuts.filter((shortcut) => shortcut.enabled !== false),
    [homeShortcuts],
  );

  const handleSync = async () => {
    try {
      await syncData();
      toast.success('Synchronisiert');
    } catch {
      toast.error('Synchronisation fehlgeschlagen');
    }
  };

  return (
    <div className="flex h-full flex-col overflow-auto bg-card" data-testid="pos-home">
      <div className="border-b border-border bg-border px-3 py-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#7c7b77]">Home page</p>
            <h1 className="mt-1 text-3xl font-black tracking-tight text-[#1d1d1f]">Start</h1>
            <p className="mt-1 text-sm text-[#666763]">
              {user?.name || 'Kellner'} · Schnellzugriff fuer Verkauf und Checkout
            </p>
          </div>

          <button
            type="button"
            onClick={handleSync}
            disabled={isSyncing}
            className="inline-flex h-11 items-center gap-2 border border-[#7f8082] bg-[#3b3b3d] px-3 text-xs font-bold uppercase tracking-[0.08em] text-white transition-colors hover:bg-[#2f2f31] disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
            Sync
          </button>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-px border border-border bg-border">
          <div className="bg-card px-3 py-2.5">
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8b8b87]">Status</p>
            <div className="mt-1 flex items-center gap-2">
              {isOnline ? (
                <Wifi className="h-4 w-4 text-[#7cc000]" />
              ) : (
                <WifiOff className="h-4 w-4 text-[#d17d17]" />
              )}
              <span className="text-sm font-bold text-[#1d1d1f]">
                {isOnline ? 'Cloud aktiv' : 'Offline'}
              </span>
            </div>
          </div>
          <div className="bg-card px-3 py-2.5">
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8b8b87]">Offen</p>
            <p className="mt-1 text-3xl font-black leading-none text-[#1d1d1f]">{pendingOrdersCount}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-px border-t border-border bg-border">
        {visibleShortcuts.map((shortcut) => {
          const Icon = resolvePosHomeIcon(shortcut.icon);
          return (
            <button
              key={shortcut.id}
              type="button"
              onClick={() => navigate(shortcut.path)}
              className="flex min-h-[172px] flex-col items-center justify-center gap-4 border-0 px-4 py-5 text-center transition-colors hover:bg-muted active:bg-border"
              style={{
                backgroundColor: shortcut.tileColor || '#ffffff',
                color: shortcut.textColor || '#1d1d1f',
              }}
            >
              <Icon className="h-14 w-14" style={{ color: shortcut.accentColor || '#2f2f31' }} strokeWidth={1.8} />
              <div>
                <p className="text-[1.35rem] font-black leading-tight">{shortcut.title}</p>
                <p className="mt-1 text-xs font-medium leading-snug text-[#6c6d68]">
                  {shortcut.description}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
